import PropTypes from 'prop-types';
import classNames from 'classnames';
import React, { Fragment, useState } from 'react';
import pluralize from 'pluralize';

import {
  CURRENCY,
  getCurrencySymbol,
} from '../../../../../../../../constants/currency';
import { getAlbumCustomPrintPrice } from '../../../../../../../../util/custom-prints';
import { getAsset } from '../../../../../../../../util/assets';
import Badge from '../../../../../../../../components/global/Badge';
import PriceDisplay from '../../../../../../../../components/global/PriceDisplay';
import Button from '../../../../../../../../components/global/Button';
import { formatPriceForDisplay } from '../../../../../../../../util/format-money';
import AddedToCartLabel from '../../PhotoPurchasingOptions/AddedToCartLabel';
import AlbumCustomPrintPreviewModal from '../components/AlbumCustomPrintOption/AlbumCustomPrintPreviewModal';
import ImagePreload from '../../../../../../../../components/global/ImagePreload';
import CardElementPhotoSelectionControl from '../../../../../../../../services/cards/components/CardElementPhoto/CardElementPhotoSelectionControl';
import { REQUIRES_PRINT_PACKAGE_ALERT_MODAL } from '../components/RequiresPrintPackageAlert';
import useOpenModal from '../../../../../../../../hooks/useOpenModal';

const propTypes = {
  className: PropTypes.string,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  disabledElement: PropTypes.node,
  photo: PropTypes.shape({
    id: PropTypes.string,
    priced_album_folder_tag: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  specialtyPrint: PropTypes.shape({
    id: PropTypes.string.isRequired,
    price: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.oneOf(Object.values(CURRENCY)),
    }),
    discount_price: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.oneOf(Object.values(CURRENCY)),
    }),
    custom_print: PropTypes.shape({
      name: PropTypes.string,
      description: PropTypes.string,
    }),
  }).isRequired,
  cartItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onSpecialtyPrintToggle: PropTypes.func.isRequired,
  hasPurchasedSubscription: PropTypes.bool,
  hasSubscriptionInCart: PropTypes.bool,
  subscriptionAvailable: PropTypes.bool,
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string,
  }),
  isAllPhotos: PropTypes.bool,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
};

const SpecialtyPrintOption = ({
  className,
  readOnly,
  disabled,
  disabledElement,
  photo,
  specialtyPrint,
  cartItem,
  onSpecialtyPrintToggle,
  hasPurchasedSubscription,
  hasSubscriptionInCart,
  subscriptionAvailable,
  albumMembershipGroup,
  isAllPhotos,
  watermarkSettings,
}) => {
  const classes = classNames({
    [className]: !!className,
    'purchasing-option-item': true,
    'purchasing-option-item-album-custom-print': true,
  });
  const { custom_print: customPrint } = specialtyPrint;
  const { name, description, preview_image: previewImage } = customPrint;
  const pricedTag = photo?.price_album_folder_tag;
  const relatedPricedTagCustomPrint = (
    pricedTag?.custom_print_album_folder_tags?.records ?? []
  ).find(
    customPrintAlbumFolderTag =>
      customPrintAlbumFolderTag.custom_print.id === customPrint.id,
  );
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectingPhotos, setSelectingPhotos] = useState(false);
  const openModal = useOpenModal();
  const price =
    relatedPricedTagCustomPrint?.price?.value > 0
      ? relatedPricedTagCustomPrint?.price?.value
      : getAlbumCustomPrintPrice(specialtyPrint);
  const discountPrice =
    relatedPricedTagCustomPrint?.price?.value > 0
      ? undefined
      : getAlbumCustomPrintPrice(specialtyPrint, true);
  const hasMultiplePhotos = specialtyPrint?.photos_no > 1;
  const hasPreviewImage = !!previewImage;
  const shouldDiscount = hasSubscriptionInCart;
  const currencySymbol = getCurrencySymbol(
    specialtyPrint?.discount_price?.currency ??
      specialtyPrint?.price?.currency ??
      CURRENCY.USD,
  );

  const onAlbumCustomPrintRequest = () => {
    if (disabled) {
      openModal(REQUIRES_PRINT_PACKAGE_ALERT_MODAL, { name });
      return;
    }

    if (hasMultiplePhotos && !cartItem) {
      setSelectingPhotos(true);
      return;
    }

    onSpecialtyPrintToggle();
  };

  return (
    <div className={classes}>
      <div>
        <div className="purchasing-option-item-info-extra">
          <Badge
            badgeStyle="yellow"
            className="purchasing-option-item-info-badge"
          >
            <PriceDisplay
              title="Custom print price"
              price={shouldDiscount && !!discountPrice ? discountPrice : price}
              oldPrice={price}
              showDiscount={shouldDiscount}
              animatedStrikethrough
              trimDecimalZeros={false}
            />
          </Badge>
        </div>

        <div className="purchasing-option-item-thumb">
          <img
            width={75}
            src={previewImage?.url ?? getAsset('/plaque.svg')}
            alt=""
          />
        </div>
      </div>

      <div className="purchasing-option-item-content-wrap">
        <div className="purchasing-option-item-title">{name}</div>
        <div className="purchasing-option-item-content mb-2">
          <p className="text-sm lh-normal text-dark text-bold">
            {description ?? ''}
            {hasMultiplePhotos ? (
              <>
                <br />
                <br />
                Pick your favorite photos to build your product!
              </>
            ) : (
              ''
            )}
          </p>

          {hasPreviewImage && (
            <p className="mb-0">
              <Button
                btnStyle="link"
                className="text-sm lh-normal text-extra-bold"
                onClick={() => setPreviewModalOpen(true)}
              >
                See sample
              </Button>
            </p>
          )}

          {discountPrice > 0 && subscriptionAvailable && (
            <p className="d-block text-sm text-italic text-bold">
              Only{' '}
              <span className="text-primary">
                {currencySymbol}
                {formatPriceForDisplay(discountPrice, true)}
              </span>{' '}
              if purchased with a Digital Photo Pass. Discount is limited to 1
              print.
            </p>
          )}
        </div>

        {disabled && disabledElement && !readOnly && <>{disabledElement}</>}

        {!readOnly && (
          <Button
            btnStyle={classNames({
              primary: !cartItem,
              info: !!cartItem,
            })}
            className={classNames({
              'purchasing-options-item-btn': true,
              'btn-purchase-options-in-cart': !!cartItem,
            })}
            onClick={onAlbumCustomPrintRequest}
            data-testid="btn-album-custom-print"
          >
            {cartItem ? (
              <AddedToCartLabel />
            ) : (
              <Fragment>
                {hasMultiplePhotos ? 'Select photos' : 'Add to cart'}
              </Fragment>
            )}
          </Button>
        )}
      </div>

      {hasPreviewImage && (
        <Fragment>
          <AlbumCustomPrintPreviewModal
            forceOpen={previewModalOpen}
            onClose={() => setPreviewModalOpen(false)}
            albumCustomPrint={specialtyPrint}
            onAlbumCustomPrintToggle={onAlbumCustomPrintRequest}
            shouldDiscount={shouldDiscount}
            albumCustomPrintInCart={!!cartItem}
            hasPurchasedSubscription={hasPurchasedSubscription}
            title="Pick your team photo"
            disableSelection
          />
          <ImagePreload urls={[previewImage.url]} />
        </Fragment>
      )}
      <CardElementPhotoSelectionControl
        title={`Pick your favorite ${pluralize(
          'photo',
          specialtyPrint.photos_no,
          true,
        )}`}
        uploadDisabled
        photosContext={{
          album_id:
            albumMembershipGroup &&
            (albumMembershipGroup.is_album
              ? albumMembershipGroup.id
              : undefined),
          album_folder_id:
            albumMembershipGroup &&
            (!albumMembershipGroup.is_album
              ? albumMembershipGroup.id
              : undefined),
          root_album_folder_id:
            albumMembershipGroup && albumMembershipGroup.is_album
              ? albumMembershipGroup.album.root_album_folder?.id
              : undefined,
          // We rely on a resolver that the membershipGroup would have by
          // default when we don't create a pseudo membership (identities).
          // This allows us to actually discern if this is a membership or not.
          is_album_membership_group: !!albumMembershipGroup?.identities,
          is_all_photos: isAllPhotos,
        }}
        forceOpen={selectingPhotos}
        onClose={() => {
          setSelectingPhotos(false);
        }}
        maxPhotosCount={specialtyPrint.photos_no}
        currentSelectedPhotos={cartItem?.photos ?? [photo]}
        onChange={photos => {
          onSpecialtyPrintToggle({ photos });
          setSelectingPhotos(false);
        }}
        watermarkSettings={watermarkSettings}
      />
    </div>
  );
};

SpecialtyPrintOption.propTypes = propTypes;

export default SpecialtyPrintOption;
